"use client";

import { useRouter, usePathname, useSearchParams } from "next/navigation";
import React, { useState, useEffect } from "react";

import { useTranslations } from "next-intl";

import { Divider } from "@/components/ui/Divider";
import { cn } from "@/utils/cn";
import { PlansFilterParams } from "@/utils/filterLayouts";
import { replaceInputValue } from "@/utils/replaceInputValue";

import styles from "./style.module.scss";

interface FilterAreaProps {
  currentFilters: PlansFilterParams;
  className?: string;
}

export const FilterArea: React.FC<FilterAreaProps> = ({
  currentFilters,
  className,
}) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Focus state
  const [focused, setFocus] = useState(false);

  // Local state for inputs to avoid constant URL updates
  const [areaMin, setAreaMin] = useState(
    currentFilters.areaMin?.toString() || "",
  );
  const [areaMax, setAreaMax] = useState(
    currentFilters.areaMax?.toString() || "",
  );

  // Update local state when currentFilters change
  useEffect(() => {
    setAreaMin(currentFilters.areaMin?.toString() || "");
    setAreaMax(currentFilters.areaMax?.toString() || "");
  }, [currentFilters.areaMin, currentFilters.areaMax]);

  const updateFilters = () => {
    const params = new URLSearchParams(searchParams.toString());

    // Remove existing area parameters
    params.delete("areaMin");
    params.delete("areaMax");

    // Add new area parameters if they have values
    const minVal = parseFloat(areaMin);
    const maxVal = parseFloat(areaMax);

    if (!isNaN(minVal) && minVal > 0) {
      params.set("areaMin", minVal.toString());
    }

    if (!isNaN(maxVal) && maxVal > 0) {
      params.set("areaMax", maxVal.toString());
    }

    router.push(`${pathname}?${params.toString()}`);
  };

  const handleAreaMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const replaceValue = replaceInputValue(value);
    setAreaMin(replaceValue);
  };

  const handleAreaMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const replaceValue = replaceInputValue(value);
    setAreaMax(replaceValue);
  };

  const handleAreaMinBlur = () => {
    updateFilters();
    setFocus(false);
  };

  const handleAreaMaxBlur = () => {
    updateFilters();
    setFocus(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      updateFilters();
    }
  };

  const clearAreaFilter = () => {
    setAreaMin("");
    setAreaMax("");

    const params = new URLSearchParams(searchParams.toString());
    params.delete("areaMin");
    params.delete("areaMax");
    router.push(`${pathname}?${params.toString()}`);
  };

  const hasActiveFilter = areaMin !== "" || areaMax !== "";

  return (
    <div className={cn(styles.filterArea, className)}>
      <div className={styles.header}>
        <div className={styles.label}>{t("Area")}</div>
        {hasActiveFilter && (
          <button
            className={styles.clearButton}
            onClick={clearAreaFilter}
            title={t("Clear")}
          >
            ×
          </button>
        )}
      </div>

      <div className={cn(styles.inputsWrapper, focused && styles.focussed)}>
        <div className={styles.inputGroup}>
          <input
            type="text"
            value={areaMin}
            onChange={handleAreaMinChange}
            onBlur={handleAreaMinBlur}
            onKeyPress={handleKeyPress}
            placeholder={t("Min")}
            className={styles.areaInput}
            onFocus={() => setFocus(true)}
          />
          <span className={styles.unit}>{t("m2")}</span>
        </div>

        <Divider
          className={"my-4 h-auto border-gray"}
          variant="vertical"
        />

        <div className={styles.inputGroup}>
          <input
            type="text"
            value={areaMax}
            onChange={handleAreaMaxChange}
            onBlur={handleAreaMaxBlur}
            onKeyPress={handleKeyPress}
            placeholder={t("Max")}
            className={styles.areaInput}
            onFocus={() => setFocus(true)}
          />
          <span className={styles.unit}>{t("m2")}</span>
        </div>
      </div>
    </div>
  );
};
